import Soundfont from 'soundfont-player'
import type { MidiNote } from '../types/songbook'

let audioCtx: AudioContext | null = null
let instrument: Soundfont.Player | null = null
let isLoading = false
let scheduledTimeouts: ReturnType<typeof setTimeout>[] = []
let isPlaying = false

function getAudioContext(): AudioContext {
  if (!audioCtx) {
    audioCtx = new AudioContext()
  }
  return audioCtx
}

async function ensureInstrument(): Promise<Soundfont.Player> {
  if (instrument) return instrument
  if (isLoading) {
    // Wait for loading to finish
    while (isLoading) {
      await new Promise((r) => setTimeout(r, 100))
    }
    return instrument!
  }

  isLoading = true
  const ctx = getAudioContext()
  instrument = await Soundfont.instrument(ctx, 'acoustic_grand_piano' as Soundfont.InstrumentName)
  isLoading = false
  return instrument
}

/**
 * Play an array of MIDI notes with tempo scaling.
 * onProgress is called with the current time position (0-1).
 */
export async function playMidiNotes(
  notes: MidiNote[],
  originalTempo: number,
  playTempo: number,
  onProgress?: (position: number) => void,
  onDone?: () => void,
): Promise<void> {
  stopMidiPlayback()

  const ctx = getAudioContext()
  if (ctx.state === 'suspended') await ctx.resume()

  const inst = await ensureInstrument()

  const tempoRatio = originalTempo / playTempo
  const maxTime = Math.max(...notes.map((n) => (n.time + n.duration) * tempoRatio))

  isPlaying = true

  for (const note of notes) {
    const startTime = note.time * tempoRatio * 1000
    const duration = note.duration * tempoRatio

    const timeout = setTimeout(() => {
      if (!isPlaying) return
      inst.play(String(note.midi), 0, {
        gain: note.velocity * 2,
        duration,
      })
    }, startTime)

    scheduledTimeouts.push(timeout)
  }

  // Progress tracking
  if (onProgress) {
    const startMs = Date.now()
    const totalMs = maxTime * 1000
    const progressInterval = setInterval(() => {
      if (!isPlaying) {
        clearInterval(progressInterval)
        return
      }
      const elapsed = Date.now() - startMs
      const pos = Math.min(elapsed / totalMs, 1)
      onProgress(pos)
      if (pos >= 1) {
        clearInterval(progressInterval)
        isPlaying = false
        if (onDone) onDone()
      }
    }, 50)

    scheduledTimeouts.push(progressInterval as unknown as ReturnType<typeof setTimeout>)
  }

  // Auto-stop after duration
  const stopTimeout = setTimeout(() => {
    isPlaying = false
    if (onDone) onDone()
  }, maxTime * 1000 + 100)

  scheduledTimeouts.push(stopTimeout)
}

export function stopMidiPlayback() {
  isPlaying = false
  for (const t of scheduledTimeouts) {
    clearTimeout(t)
  }
  scheduledTimeouts = []
  if (instrument) {
    instrument.stop()
  }
}

export function isMidiPlaying(): boolean {
  return isPlaying
}
